// src/providers/notifications.jsx
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import { useAuth } from 'lib/auth';
import { NOTIFICATIONS_SOCKET_URL } from 'config';

const NotificationsContext = createContext(null);

export function NotificationsProvider({ children }) {
  const { user, isLoading } = useAuth(); // IMPORTANT: use isLoading
  const socketRef = useRef(null);

  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const SOCKET_URL = NOTIFICATIONS_SOCKET_URL;

  useEffect(() => {
    // WAIT for auth to finish loading
    if (isLoading) return;

    // ONLY connect when user is available
    if (!user?._id) {
      console.log('[Notifications] No user, skipping socket connect');
      return;
    }

    console.log('[Notifications] Connecting as user:', user._id);

    // cleanup old socket
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const path =
      process.env.REACT_APP_SOCKET_ENV === 'development'
        ? '/socket.io'
        : '/notifications/socket.io';

    const socket = io(SOCKET_URL, {
      path,
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[Notifications] Socket connected');
      setConnected(true);
      socket.emit('identify', { userId: user._id });
      console.log(`Identifying as ${user._id}`);
    });

    socket.on('disconnect', () => {
      console.log('[Notifications] Socket disconnected');
      setConnected(false);
    });

    socket.on('notification', (data) => {
      console.log('[Notifications] Received notification:', data);
      setNotifications((prev) => [{ ...data, id: Date.now() }, ...prev]);
    });

    return () => {
      console.log('[Notifications] Cleanup disconnect');
      socket.disconnect();
    };
  }, [isLoading, user?._id]);

  return (
    <NotificationsContext.Provider value={{ connected, notifications, socket: socketRef.current }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationsContext);
